import type { ReactNode } from "react";

export type PhonePreviewButton = {
  kind: "url" | "phone" | "quick_reply" | "otp" | "flow";
  text: string;
};

type HeaderFormat = "none" | "text" | "image" | "video" | "document";

type WhatsAppPhonePreviewProps = {
  businessName?: string;
  headerFormat?: HeaderFormat;
  headerText?: string;
  headerMediaUrl?: string;
  headerFileName?: string;
  body?: string;
  footer?: string;
  buttons?: PhonePreviewButton[];
};

function formatWhatsApp(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(/\*(.+?)\*/g, "<strong>$1</strong>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/~(.+?)~/g, "<s>$1</s>")
    .replace(/\n/g, "<br />");
}

function buttonLabel(button: PhonePreviewButton): string {
  if (button.text.trim()) return button.text.trim();
  if (button.kind === "otp") return "Copy code";
  if (button.kind === "url") return "Open link";
  if (button.kind === "phone") return "Call";
  if (button.kind === "flow") return "Open";
  return "Reply";
}

function ButtonIcon({ kind }: { kind: PhonePreviewButton["kind"] }) {
  if (kind === "url" || kind === "flow") {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 5h5v5" />
        <path d="M19 5l-9 9" />
        <path d="M19 13v6H5V5h6" />
      </svg>
    );
  }
  if (kind === "phone") {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M7 3h4l2 5-3 2a12 12 0 0 0 6 6l2-3 5 2v4c0 1-1 2-2 2C10 21 3 14 3 5c0-1 1-2 2-2z" />
      </svg>
    );
  }
  if (kind === "otp") {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="8" y="8" width="12" height="12" rx="2" />
        <path d="M4 16V6a2 2 0 0 1 2-2h10" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 10l-5 2 5 2" />
      <path d="M15 10l5 2-5 2" />
    </svg>
  );
}

export default function WhatsAppPhonePreview({
  businessName,
  headerFormat = "none",
  headerText = "",
  headerMediaUrl = "",
  headerFileName = "",
  body = "",
  footer = "",
  buttons = [],
}: WhatsAppPhonePreviewProps) {
  const name = businessName?.trim() || "WhatsApp";
  const time = new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  let header: ReactNode = null;
  if (headerFormat === "text" && headerText.trim()) {
    header = <p className="px-2 pt-2 text-[13px] font-semibold text-[#111b21]">{headerText}</p>;
  } else if (headerFormat === "image") {
    header = headerMediaUrl ? (
      <img src={headerMediaUrl} alt="" className="h-36 w-full object-cover" />
    ) : (
      <div className="flex h-36 items-center justify-center bg-[#d1d7db] text-xs text-[#54656f]">Image</div>
    );
  } else if (headerFormat === "video") {
    header = headerMediaUrl ? (
      <video src={headerMediaUrl} className="h-36 w-full bg-black object-cover" />
    ) : (
      <div className="flex h-36 items-center justify-center bg-[#111b21] text-xs text-white">Video</div>
    );
  } else if (headerFormat === "document") {
    header = (
      <div className="mx-1 mt-1 flex items-center gap-2 rounded-md bg-[#f0f2f5] px-2 py-2">
        <span className="text-lg">📄</span>
        <span className="truncate text-xs text-[#111b21]">{headerFileName || "Document"}</span>
      </div>
    );
  }

  return (
    <div className="mx-auto w-[280px]">
      <div className="rounded-[2rem] border-[10px] border-[#111b21] bg-[#efeae2] shadow-xl">
        <div className="flex items-center justify-between bg-[#075e54] px-3 py-2 text-white">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{name}</p>
            <p className="text-[10px] text-white/80">online</p>
          </div>
          <p className="text-[10px]">{time}</p>
        </div>
        <div className="h-[460px] overflow-y-auto px-2 py-3">
          <div className="max-w-[92%] overflow-hidden rounded-lg rounded-tl-none bg-white shadow-sm">
            {header}
            <div className="px-2 pb-1 pt-1.5">
              {body.trim() ? (
                <p
                  className="whitespace-pre-wrap break-words text-[13px] leading-5 text-[#111b21]"
                  dangerouslySetInnerHTML={{ __html: formatWhatsApp(body) }}
                />
              ) : (
                <p className="text-[13px] text-[#8696a0]">Message</p>
              )}
              {footer.trim() ? <p className="mt-1 text-[11px] text-[#667781]">{footer}</p> : null}
              <p className="mt-1 text-right text-[10px] text-[#667781]">{time}</p>
            </div>
            {buttons.map((button, index) => (
              <div
                key={`${button.kind}-${index}`}
                className="flex items-center justify-center gap-2 border-t border-[#e9edef] px-2 py-2 text-[13px] font-medium text-[#027eb5]"
              >
                <ButtonIcon kind={button.kind} />
                <span className="truncate">{buttonLabel(button)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="mt-2 text-center text-xs text-gray-400">What the customer receives</p>
    </div>
  );
}

type TemplateComponent = {
  type?: string;
  format?: string;
  text?: string;
  example?: { header_handle?: string[] };
  buttons?: Array<{ type?: string; text?: string }>;
};

export function phonePreviewFromTemplate(template: { components?: TemplateComponent[] } | null) {
  const components = template?.components || [];
  const header = components.find((item) => item.type === "HEADER");
  const body = components.find((item) => item.type === "BODY");
  const footer = components.find((item) => item.type === "FOOTER");
  const buttonBlock = components.find((item) => item.type === "BUTTONS");
  const format = String(header?.format || "NONE").toLowerCase();
  const headerFormat: HeaderFormat =
    format === "image" || format === "video" || format === "document" || format === "text" ? format : "none";
  const buttons: PhonePreviewButton[] = (buttonBlock?.buttons || []).map((button) => {
    const type = String(button.type || "").toUpperCase();
    const kind: PhonePreviewButton["kind"] =
      type === "URL" ? "url" : type === "PHONE_NUMBER" ? "phone" : type === "OTP" ? "otp" : type === "FLOW" ? "flow" : "quick_reply";
    return { kind, text: button.text || "" };
  });
  return {
    headerFormat,
    headerText: header?.text || "",
    headerMediaUrl: header?.example?.header_handle?.[0] || "",
    body: body?.text || "",
    footer: footer?.text || "",
    buttons,
  };
}
